import api, { fetchStream } from './api';
import logger from '../utils/logger';

export default {
    /**
     * Search for artists
     * @param {string} query
     * @param {number} limit
     * @param {number} offset
     */
    async searchArtists(query, limit = 10, offset = 0) {
        try {
            const response = await api.get('/artists/search', {
                params: { q: query, limit, offset }
            });
            return response; // api.get returns parsed JSON directly
        } catch (error) {
            logger.error('Error searching artists:', error);
            throw error;
        }
    },

    /**
     * Search for albums
     * @param {string} query
     * @param {number} limit
     * @param {number} offset
     * @param {boolean} localOnly
     */
    async searchAlbums(query, limit = 10, offset = 0, localOnly = false, streamCallbacks = null) {
        if (streamCallbacks) {
            const params = new URLSearchParams({
                q: query,
                limit: limit.toString(),
                offset: offset.toString(),
                localOnly: localOnly ? 'true' : 'false'
            });
            const url = `/albums/search?${params.toString()}`;
            return fetchStream(url, streamCallbacks);
        }
        try {
            const response = await api.get('/albums/search', {
                params: { q: query, limit, offset, localOnly }
            });
            return response;
        } catch (error) {
            logger.error('Error searching albums:', error);
            throw error;
        }
    },

    /**
     * Search for concerts
     * @param {string} query
     * @param {number} limit
     * @param {number} offset
     */
    async searchConcerts(query, limit = 10, offset = 0, streamCallbacks = null) {
        const normalized = normalizeSearchOptions(streamCallbacks);
        if (normalized.streamCallbacks) {
            const params = new URLSearchParams({
                q: query,
                limit: limit.toString(),
                offset: offset.toString(),
                forceExternal: normalized.forceExternal ? 'true' : 'false'
            });
            const url = `/concerts/search?${params.toString()}`;
            return fetchStream(url, normalized.streamCallbacks);
        }
        try {
            const response = await api.get('/concerts/search', {
                params: { q: query, limit, offset, forceExternal: normalized.forceExternal }
            });
            return response;
        } catch (error) {
            logger.error('Error searching concerts:', error);
            throw error;
        }
    },

    /**
     * Search for festivals
     * @param {string} query
     * @param {number} limit
     * @param {number} offset
     */
    async searchFestivals(query, limit = 10, offset = 0, streamCallbacks = null) {
        const normalized = normalizeSearchOptions(streamCallbacks);
        if (normalized.streamCallbacks) {
            const params = new URLSearchParams({
                q: query,
                limit: limit.toString(),
                offset: offset.toString(),
                forceExternal: normalized.forceExternal ? 'true' : 'false'
            });
            const url = `/festivals/search?${params.toString()}`;
            return fetchStream(url, normalized.streamCallbacks);
        }
        try {
            const response = await api.get('/festivals/search', {
                params: { q: query, limit, offset, forceExternal: normalized.forceExternal }
            });
            return response;
        } catch (error) {
            logger.error('Error searching festivals:', error);
            throw error;
        }
    },

    /**
     * Perform a unified search across all entities (limited results for each)
     * @param {string} query
     */
    async searchAll(query) {
        // Execute searches in parallel
        const [artists, albums, concerts, festivals] = await Promise.allSettled([
            this.searchArtists(query, 5),
            this.searchAlbums(query, 5),
            this.searchConcerts(query, 5),
            this.searchFestivals(query, 5)
        ]);

        // Helper to safely extract value or return default empty structure
        const getValue = (result, key = 'items', defaultVal = []) =>
            result.status === 'fulfilled' ? (result.value?.[key] || defaultVal) : defaultVal;

        return {
            artists: getValue(artists, 'items'),
            albums: getValue(albums, 'items'),
            concerts: getValue(concerts, 'items'),
            festivals: getValue(festivals, 'items')
        };
    },

    /**
     * Search albums by title
     * @param {string} title
     * @param {number} limit
     * @param {number} offset
     * @param {boolean} localOnly
     */
    async searchAlbumsByTitle(title, limit = 10, offset = 0, localOnly = false) {
        try {
            const response = await api.get('/albums/search/by-album', {
                params: { title, limit, offset, localOnly }
            });
            return response;
        } catch (error) {
            logger.error('Error searching albums by title:', error);
            throw error;
        }
    },

    /**
     * Search albums by artist
     * @param {string} artist
     * @param {number} limit
     * @param {number} offset
     * @param {boolean} localOnly
     */
    async searchAlbumsByArtist(artist, limit = 10, offset = 0, localOnly = false) {
        try {
            const response = await api.get('/albums/search/by-artist', {
                params: { artist, limit, offset, localOnly }
            });
            return response;
        } catch (error) {
            logger.error('Error searching albums by artist:', error);
            throw error;
        }
    },

    /**
     * Search albums by both artist and title
     * @param {string} artist
     * @param {string} title
     * @param {number} limit
     * @param {number} offset
     * @param {boolean} localOnly
     */
    async searchAlbumsByBoth(artist, title, limit = 10, offset = 0, localOnly = false) {
        try {
            const response = await api.get('/albums/search/by-both', {
                params: { artist, title, limit, offset, localOnly }
            });
            return response;
        } catch (error) {
            logger.error('Error searching albums by artist and title:', error);
            throw error;
        }
    }
};

function normalizeSearchOptions(optionsOrStream) {
    if (!optionsOrStream) {
        return { streamCallbacks: null, forceExternal: false };
    }

    const isStreamCallbacks = Boolean(
        optionsOrStream.onData || optionsOrStream.onError || optionsOrStream.onDone || optionsOrStream.onOpen
    );

    if (isStreamCallbacks) {
        return { streamCallbacks: optionsOrStream, forceExternal: false };
    }

    return {
        streamCallbacks: optionsOrStream.streamCallbacks || null,
        forceExternal: Boolean(optionsOrStream.forceExternal)
    };
}
